// Assign an UNASSIGNED device from the Message Box's centre pane (both shells).
//
// The short form of Device Admin's assign panel: Master user -> one of their
// locations -> a room (or Common device, or a new room) -> Save. It writes
// through the SAME core/devices/deviceAdmin.assignDevice, so the install row and
// all three mappings (user->location, user->room, user->device) are written
// together — doing only the row is the half-assignment nobody can see.
//
// ⚠️ IT RE-READS THE DEVICE FRESH WHEN OPENED, and refuses if it is assigned by
// then: the console's row can be minutes old, and assigning off it would MOVE a
// device somebody else has just placed. Moving an assigned device stays Device
// Admin's job (Unassign first).
//
// New users and new locations are not offered here — they need an address, a
// PIN and the mobile-number upsert guard; that is Device Admin.
//
// Super Admin only — it writes ownership.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, Autocomplete, MenuItem, Box, Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { useApp } from '../../core/state/AppContext';
import {
  findDevice, getAllUsers, userLabel, userOptionKey, userSearchText,
  locationsOf, roomsOf, createRoom, assignDevice, COMMON_ROOM_ID,
} from '../../core/devices/deviceAdmin';
import { tokenFilter } from '../../core/utils/search';
import { loadNameIndex } from '../../core/devices/nameIndex';
import {
  isOneEndpoint, spokenName, normaliseName, clashMessage,
} from '../../core/devices/alexaNames';
import { FIT_SM } from './fitDialog';

const NEW = '__new__';
const MAX_USERS = 50;   // 450+ users; draw only what the typing narrows to
const filterUsers = tokenFilter(userSearchText);

/**
 * `suggest` ({ userId, locationId }) PRE-PICKS the owner and location without
 * assigning anything — the Message Box's network trace knows which house an
 * unassigned device is probably in (core/devices/siteGuess), and retyping that
 * guess into this dialog is the step that makes people not bother. The room is
 * still chosen by hand: no trace can know which room, and Save is still Save.
 */
export default function AssignDeviceButton({
  uniqueNumber, onDone, sx, suggest = null,
}) {
  const { isSuperAdmin } = useApp();
  const uno = String(uniqueNumber || '').trim();
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [found, setFound] = useState(null);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');      // '' = unset; 0 = Common device
  const [newRoom, setNewRoom] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  // Locations follow the user. `alive` — a second pick must not land under the first.
  useEffect(() => {
    setLocationId(''); setRooms([]); setRoomId('');
    if (!user) { setLocations([]); return undefined; }
    let alive = true;
    locationsOf(user.UserId).then((list) => {
      if (!alive) return;
      setLocations(list);
      const hinted = suggest && list.find((l) => String(l.LocationID) === String(suggest.locationId));
      if (hinted) setLocationId(hinted.LocationID);
      else if (list.length === 1) setLocationId(list[0].LocationID);
    });
    return () => { alive = false; };
    // `suggest` is read above (the traced location) — it is a prop, so it is
    // named here rather than captured from the first render.
  }, [user, suggest]);

  // Rooms follow the location.
  useEffect(() => {
    setRoomId('');
    if (!user || !locationId) { setRooms([]); return undefined; }
    let alive = true;
    roomsOf(user.UserId, locationId).then((list) => { if (alive) setRooms(list); });
    return () => { alive = false; };
  }, [user, locationId]);

  const canSave = useMemo(() => !!(found?.found && !found.assigned && user && locationId
    && roomId !== '' && (roomId !== NEW || newRoom.trim())), [found, user, locationId, roomId, newRoom]);

  if (!isSuperAdmin || !uno) return null;

  const start = async () => {
    setChecking(true); setError(''); setDone('');
    setUser(null); setNewRoom('');
    const [r, u] = await Promise.all([
      findDevice(uno).catch(() => null),
      users.length ? Promise.resolve(null) : getAllUsers().catch(() => null),
    ]);
    const list = u ? (Array.isArray(u.data) ? u.data : []) : users;
    if (u) setUsers(list);
    // The traced owner, pre-picked (never saved) — see `suggest`.
    if (suggest && suggest.userId) {
      setUser(list.find((x) => Number(x.UserId) === Number(suggest.userId)) || null);
    }
    setFound(r);
    setDeviceName(r?.device?.DeviceName || '');
    setChecking(false);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true); setError('');
    try {
      const loc = locations.find((l) => String(l.LocationID) === String(locationId));
      const roomLabel = roomId === NEW ? newRoom.trim()
        : rooms.find((x) => String(x.RoomID) === String(roomId))?.RoomName;
      /*
       * A curtain / lock / RGB / WWH light is ONE Alexa endpoint, spoken
       * "<Room> <Device name>" (Common device: the location) — so that name must
       * not already be spoken in the NEW owner's account. A switch panel's own
       * name is never spoken; its buttons keep their names. See alexaNames.
       */
      if (isOneEndpoint(found.device?.DeviceID)) {
        const place = Number(roomId) === COMMON_ROOM_ID ? loc?.LocationName : roomLabel;
        const spoken = spokenName(place, deviceName);
        const index = spoken ? await loadNameIndex(user.UserId).catch(() => null) : null;
        const clash = index && (index.get(normaliseName(spoken)) || [])
          .find((e) => e.uno !== uno);
        if (clash) { setError(clashMessage(clash)); return; }
      }
      let rmId = roomId;
      if (roomId === NEW) rmId = await createRoom(user.UserId, locationId, newRoom.trim());
      await assignDevice({
        device: found.device,
        uniqueNumber: uno,
        userId: user.UserId,
        locationId,
        roomId: rmId,
        deviceName,
      });
      const room = Number(rmId) === COMMON_ROOM_ID ? 'Common device' : roomLabel;
      setDone(`Assigned to ${userLabel(user)} — ${loc?.LocationName || 'location'} / ${room || 'room'}.`);
      if (onDone) onDone();
    } catch (e) {
      setError(e.message || 'Could not assign. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const ready = found?.found && !found.assigned && !done;
  return (
    <>
      <Button
        size="small" variant="contained" color="success" sx={sx}
        startIcon={checking ? <CircularProgress size={12} /> : <AddLinkIcon sx={{ fontSize: 14 }} />}
        disabled={checking}
        onClick={start}
      >
        Assign
      </Button>

      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} {...FIT_SM}>
        <DialogTitle sx={{ pb: 0.5 }}>{`Assign ${uno}`}</DialogTitle>
        <DialogContent>
          {!found?.found && <Alert severity="warning">{`No device ${uno} on the server.`}</Alert>}
          {found?.found && found.assigned && !done && (
            <Alert severity="info">
              {`Already assigned — ${found.device?.DeviceMasterUser || 'an owner'}, ${found.device?.LocationName || 'a location'}. Move it from Device Admin (Unassign first).`}
            </Alert>
          )}
          {done && <Alert severity="success">{done}</Alert>}

          {ready && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <TextField
                size="small" label="Device name" value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
              />
              <Autocomplete
                size="small" options={users} value={user}
                onChange={(_, v) => setUser(v)}
                getOptionLabel={(u) => userLabel(u)}
                isOptionEqualToValue={(a, b) => userOptionKey(a) === userOptionKey(b)}
                filterOptions={(opts, st) => filterUsers(opts, st).slice(0, MAX_USERS)}
                // ⚠️ Keyed by id — 457 users share 432 names (see userOptionKey).
                renderOption={(props, u) => (
                  <li {...props} key={userOptionKey(u)}>
                    <Box>
                      <Typography variant="body2">{userLabel(u)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${u.MobileNumber || 'no mobile'} · ID ${u.UserId}`}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(p) => (
                  <TextField
                    {...p} label="Master user" autoFocus
                    helperText={user ? `${user.MobileNumber || 'no mobile'} · ID ${user.UserId}` : 'Type a name, mobile or id'}
                  />
                )}
              />
              <TextField
                select size="small" label="Location" value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={!user}
                helperText={user && !locations.length ? 'This user has no location — create one in Device Admin.' : ' '}
              >
                {locations.map((l) => (
                  <MenuItem key={l.LocationID} value={l.LocationID}>{l.LocationName}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Room" value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                disabled={!locationId}
              >
                {/* Room 0 is a real assignment — the device shows under Common Devices. */}
                <MenuItem value={COMMON_ROOM_ID}>Common device</MenuItem>
                {rooms.map((r) => (
                  <MenuItem key={r.RoomID} value={r.RoomID}>{r.RoomName}</MenuItem>
                ))}
                <MenuItem value={NEW}>＋ New room…</MenuItem>
              </TextField>
              {roomId === NEW && (
                <TextField
                  size="small" label="New room" value={newRoom}
                  onChange={(e) => setNewRoom(e.target.value)}
                />
              )}
              {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {done || !ready ? (
            <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button variant="contained" color="success" onClick={save} disabled={busy || !canSave}>
                {busy ? <CircularProgress size={18} /> : 'Assign'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
